import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';

import StarmaniaApi from '@deezer/next/services/api/Starmania';

import {useToast} from '@deezer/next/shared/hooks/useToast';

import {
	AddEditLabelFormCard,
	AddEditManagerArtistFormCard,
	ArtistSocialFormCard,
	ClaimTypeFormCard,
	LabelListFormCard,
	ManagerArtistListFormCard,
	NewArtistFormCard,
	NewProviderFormCard,
} from '@deezer/next/features/ClaimAccess/forms';

import {
	defaultArtistFormData,
	defaultLabelFormData,
	defaultManagerFormData,
	defaultProviderFormData,
} from '@deezer/next/features/ClaimAccess/constants';

import type {
	ArtistFormData,
	ClaimFormData,
	FormFieldChange,
	LabelFormData,
	ManagerArtist,
	ManagerFormData,
	ProviderFormData,
	SubLabel,
} from '@deezer/next/features/ClaimAccess/types';

import {
	ClaimAccessType,
	ClaimScreen,
} from '@deezer/next/features/ClaimAccess/types';

import {useFetchUserMe, useUser} from '@deezer/next/contexts/main/hooks';

import {getClaimAccessTypeAsString} from '@deezer/next/shared/utils/user';

import {StakeholderType} from '@deezer/next/contexts/main/types';

import {getErrorContent} from '@deezer/next/services/api/utils';

import {Routes} from '@constants/routes';

import NavigationLayout from '@deezer/next/shared/layouts/NavigationLayout';

import type {Release} from '@deezer/next/services/api/Starmania/requests/album/types';

import {AlbumStatus} from '@deezer/next/services/api/Starmania/requests/album/types';

import {
	formatDataHelper,
	getDefaultClaimDataState,
} from '@deezer/next/features/ClaimAccess/utils';

import {getServerSideTranslationsWrapper} from '@deezer/next/shared/utils/next';

import {uniqStable} from '@deezer/next/shared/utils/set';

import ArtistDiscographyLiveCard from '@deezer/next/features/ClaimAccess/forms/Artist/ArtistDiscographyLiveCard';

import ArtistDiscographyTakedownCard from '@deezer/next/features/ClaimAccess/forms/Artist/ArtistDiscographyTakedownCard';

import useClaimNavigation from '@deezer/next/features/ClaimAccess/hooks/useClaimNavigation';

const ClaimAccess = () => {
	const {t} = useTranslation('d4c');

	const user = useUser();

	const router = useRouter();

	const fetchUserMe = useFetchUserMe();

	const toast = useToast();

	const {currentScreen, claimType, setClaimType, goToScreen, goBack, goNext} =
		useClaimNavigation(user);

	const [formData, setFormData] = useState<ClaimFormData>(() =>
		getDefaultClaimDataState(user?.stakeholderType),
	);

	const [hasDiscographyRights, setHasDiscographyRights] = useState(false);

	const [artistComment, setArtistComment] = useState('');

	const [artistReleases, setArtistReleases] = useState<Release[]>([]);

	const [artistSelectedReleaseIds, setArtistSelectedReleaseIds] = useState<
		number[] | null
	>(null);

	const [isLoadingArtistDiscography, setIsLoadingArtistDiscography] =
		useState(false);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const [artistEdit, setArtistEdit] = useState<ManagerArtist | undefined>(
		undefined,
	);

	const [labelEdit, setLabelEdit] = useState<SubLabel | undefined>(undefined);

	const artistId = useMemo(
		() => (formData as ArtistFormData).artist?.id,
		[formData],
	);

	const [previousArtistId, setPreviousArtistId] = useState(artistId);

	if (!Object.is(artistId, previousArtistId)) {
		setPreviousArtistId(artistId);

		setArtistReleases([]);
		setHasDiscographyRights(false);
		setArtistSelectedReleaseIds([]);
		setArtistComment('');
	}

	const artistUpc = useMemo(
		() => (formData as ArtistFormData)?.artistUpc,
		[formData],
	);

	const hasTakedownReleases = useMemo(
		() =>
			artistReleases.some(release => release.status === AlbumStatus.TAKEDOWN),
		[artistReleases],
	);

	const lockedReleaseIds = useMemo(() => {
		const target = artistUpc;

		if (!target) {
			return [];
		}

		return artistReleases
			.filter(release => release.upc === target)
			.map(release => release.id);
	}, [artistReleases, artistUpc]);

	const lockedReleaseIdSet = useMemo(
		() => new Set<number>(lockedReleaseIds),
		[lockedReleaseIds],
	);

	const handleClaimTypeSave = useCallback(() => {
		let screen = ClaimScreen.SELECT_CLAIM_TYPE;

		switch (claimType) {
			case ClaimAccessType.MANAGER:
				screen = ClaimScreen.MANAGER_ARTIST_LIST;

				setFormData(defaultManagerFormData);

				break;

			case ClaimAccessType.LABEL:
				screen = ClaimScreen.LABEL_LIST;

				setFormData(defaultLabelFormData);

				break;

			case ClaimAccessType.PROVIDER:
				screen = ClaimScreen.NEW_PROVIDER;

				setFormData(defaultProviderFormData);

				break;

			case ClaimAccessType.ARTIST:
				screen = ClaimScreen.NEW_ARTIST;

				setFormData(defaultArtistFormData);

				break;

			default:
				break;
		}

		goToScreen(screen);
	}, [claimType, goToScreen]);

	const handleAddManagerArtist = useCallback(() => {
		goToScreen(ClaimScreen.MANAGER_ADD_ARTIST);
	}, [goToScreen]);

	const handleEditManagerArtist = useCallback(
		(artist: ManagerArtist) => {
			setArtistEdit(artist);

			goToScreen(ClaimScreen.MANAGER_EDIT_ARTIST);
		},
		[goToScreen],
	);

	const handleAddLabel = useCallback(() => {
		goToScreen(ClaimScreen.LABEL_ADD);
	}, [goToScreen]);

	const handleEditLabel = useCallback(
		(label: SubLabel) => {
			setLabelEdit(label);

			goToScreen(ClaimScreen.LABEL_EDIT);
		},
		[goToScreen],
	);

	const handleSaveManagerArtist = useCallback(
		(artist: ManagerArtist, isEdition: boolean) => {
			setFormData(previousState => {
				if (isEdition) {
					return {
						...previousState,

						managerArtists: (
							previousState as ManagerFormData
						).managerArtists.map(currentArtist =>
							currentArtist.id !== artist.id ? currentArtist : artist,
						),
					};
				}

				return {
					...previousState,

					managerArtists: [
						...(previousState as ManagerFormData).managerArtists,

						artist,
					],
				};
			});

			goBack();
		},
		[goBack],
	);

	const handleSaveLabel = useCallback(
		(label: SubLabel, isEdition: boolean) => {
			setFormData(previousState => {
				if (isEdition) {
					return {
						...previousState,

						labelSubLabels: (previousState as LabelFormData).labelSubLabels.map(
							currentLabel =>
								currentLabel.id !== label.id ? currentLabel : label,
						),
					};
				}

				return {
					...previousState,

					labelSubLabels: [
						...(previousState as LabelFormData).labelSubLabels,

						label,
					],
				};
			});

			goBack();
		},
		[goBack],
	);

	const onFormFieldChange: FormFieldChange<ClaimFormData> = useCallback(
		<T extends keyof ClaimFormData>(key: T, value: ClaimFormData[T]) => {
			setFormData(previousFormData => ({
				...previousFormData,
				[key]: value,
			}));
		},
		[],
	);

	const handleSelectedReleaseIdsChange = useCallback(
		(id: number) => {
			if (lockedReleaseIdSet.has(id)) {
				return;
			}

			setArtistSelectedReleaseIds(previous => {
				const current = previous ?? [];

				const next = current.includes(id)
					? current.filter(currentId => currentId !== id)
					: [...current, id];

				return uniqStable([...next, ...lockedReleaseIds]);
			});
		},
		[lockedReleaseIdSet, lockedReleaseIds],
	);

	const handleAllSelectedReleaseIdsChange = useCallback(
		(ids: number[]) => {
			setArtistSelectedReleaseIds(previous => {
				const current = previous ?? [];

				const target = uniqStable(ids);

				if (target.length === 0) {
					return previous;
				}

				const selectableTarget = target.filter(
					id => !lockedReleaseIdSet.has(id),
				);
				if (selectableTarget.length === 0) {
					return uniqStable([...current, ...lockedReleaseIds]);
				}

				const allSelectableSelected = selectableTarget.every(id =>
					current.includes(id),
				);

				let next: number[];
				if (allSelectableSelected) {
					next = current.filter(id => !selectableTarget.includes(id));
				} else {
					const missing = selectableTarget.filter(id => !current.includes(id));
					next = missing.length === 0 ? current : [...current, ...missing];
				}

				return uniqStable([...next, ...lockedReleaseIds]);
			});
		},
		[lockedReleaseIdSet, lockedReleaseIds],
	);

	const handleArtistCommentChange = useCallback((comment: string) => {
		setArtistComment(comment);
	}, []);

	const handleArtistContinue = useCallback(async () => {
		try {
			setIsLoadingArtistDiscography(true);
			setArtistReleases([]);
			setHasDiscographyRights(false);
			setArtistSelectedReleaseIds([]);

			const discography = await StarmaniaApi.claim.getDiscographies(
				String(artistId),
			);

			setHasDiscographyRights(discography.meta.enabled);
			setArtistReleases(discography.data);

			if (discography.meta.enabled) {
				setArtistSelectedReleaseIds(discography.data.map(({id}) => id));
			} else {
				setArtistSelectedReleaseIds([]);
			}

			goNext();
		} catch {
			setHasDiscographyRights(false);
			setArtistReleases([]);
			setArtistSelectedReleaseIds([]);
			goNext();
		} finally {
			setIsLoadingArtistDiscography(false);
		}
	}, [artistId, goNext]);

	const handleSubmit = useCallback(async () => {
		try {
			if (!user) {
				return;
			}

			setIsSubmitting(true);

			const claimAccessType = getClaimAccessTypeAsString(user.stakeholderType);

			if (!claimAccessType) {
				return;
			}

			const shouldSendArtistExtra =
				user.stakeholderType === StakeholderType.ARTIST && hasDiscographyRights;

			const extra = shouldSendArtistExtra
				? {
						albumIds: (artistSelectedReleaseIds ?? []).map(String),
						comment: artistComment || null,
					}
				: undefined;

			if (shouldSendArtistExtra && extra?.albumIds.length === 0) {
				return;
			}

			const formattedData = formatDataHelper(formData, claimAccessType, extra);

			if (!formattedData) {
				return;
			}

			await StarmaniaApi.claim.postClaim(formattedData, claimAccessType);

			await fetchUserMe();

			toast({
				description: t('toastmessage_text_accessrequestsent_d4c-web'),
				status: 'success',
			});

			void router.push(Routes.REQUEST_MONITORING);
		} catch (error: any) {
			const errorCode = error.response?.data?.errors[0]?.code;

			const {title, description} = getErrorContent(errorCode, t);

			toast({
				title,
				description,
				status: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	}, [
		user,
		formData,
		hasDiscographyRights,
		artistComment,
		artistSelectedReleaseIds,
		fetchUserMe,
		toast,
		t,
		router,
	]);

	const screenContent = useMemo(() => {
		switch (currentScreen) {
			case ClaimScreen.SELECT_CLAIM_TYPE:
				return (
					<ClaimTypeFormCard
						claimType={claimType}
						onClaimTypeChange={setClaimType}
						onContinue={handleClaimTypeSave}
					/>
				);

			case ClaimScreen.NEW_ARTIST:
				return (
					<NewArtistFormCard
						formData={formData as ArtistFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<ArtistFormData>
						}
						onContinue={handleArtistContinue}
						onBack={goBack}
					/>
				);

			case ClaimScreen.ARTIST_SOCIALS:
				return (
					<ArtistSocialFormCard
						formData={formData as ArtistFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<ArtistFormData>
						}
						onSubmit={handleSubmit}
						onContinue={goNext}
						onBack={goBack}
						hasDiscographyRights={hasDiscographyRights}
					/>
				);

			case ClaimScreen.ARTIST_DISCOGRAPHY_LIVE:
				return (
					<ArtistDiscographyLiveCard
						releases={artistReleases}
						artistComment={artistComment}
						selectedReleaseIds={artistSelectedReleaseIds || []}
						lockedReleaseIdSet={lockedReleaseIdSet}
						onAllSelectedReleaseIdsChange={handleAllSelectedReleaseIdsChange}
						onSelectedReleaseIdsChange={handleSelectedReleaseIdsChange}
						onArtistCommentChange={handleArtistCommentChange}
						onBack={goBack}
						onSubmit={handleSubmit}
						onContinue={goNext}
						isLoading={isLoadingArtistDiscography}
						hasTakedownReleases={hasTakedownReleases}
					/>
				);

			case ClaimScreen.ARTIST_DISCOGRAPHY_TAKEDOWN:
				return (
					<ArtistDiscographyTakedownCard
						releases={artistReleases}
						artistComment={artistComment}
						selectedReleaseIds={artistSelectedReleaseIds || []}
						lockedReleaseIdSet={lockedReleaseIdSet}
						onAllSelectedReleaseIdsChange={handleAllSelectedReleaseIdsChange}
						onSelectedReleaseIdsChange={handleSelectedReleaseIdsChange}
						onArtistCommentChange={handleArtistCommentChange}
						onBack={goBack}
						onSubmit={handleSubmit}
						isLoading={isLoadingArtistDiscography}
						isSubmitting={isSubmitting}
					/>
				);

			case ClaimScreen.NEW_PROVIDER:
				return (
					<NewProviderFormCard
						formData={formData as ProviderFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<ProviderFormData>
						}
						onSubmit={handleSubmit}
						onBack={goBack}
						isSubmitting={isSubmitting}
					/>
				);

			case ClaimScreen.MANAGER_ARTIST_LIST:
				return (
					<ManagerArtistListFormCard
						formData={formData as ManagerFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<ManagerFormData>
						}
						onAddArtist={handleAddManagerArtist}
						onEditArtist={handleEditManagerArtist}
						onBack={goBack}
						onSubmit={handleSubmit}
						isSubmitting={isSubmitting}
					/>
				);

			case ClaimScreen.MANAGER_ADD_ARTIST:
				return (
					<AddEditManagerArtistFormCard
						formData={formData as ManagerFormData}
						onBack={goBack}
						onSave={handleSaveManagerArtist}
						edit={false}
					/>
				);

			case ClaimScreen.MANAGER_EDIT_ARTIST:
				return (
					<AddEditManagerArtistFormCard
						formData={formData as ManagerFormData}
						onBack={goBack}
						onSave={handleSaveManagerArtist}
						selectedArtist={artistEdit}
						edit
					/>
				);

			case ClaimScreen.LABEL_LIST:
				return (
					<LabelListFormCard
						formData={formData as LabelFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<LabelFormData>
						}
						onAddLabel={handleAddLabel}
						onEditLabel={handleEditLabel}
						onBack={goBack}
						onSubmit={handleSubmit}
						isSubmitting={isSubmitting}
					/>
				);

			case ClaimScreen.LABEL_ADD:
				return (
					<AddEditLabelFormCard
						formData={formData as LabelFormData}
						onBack={goBack}
						onSave={handleSaveLabel}
						edit={false}
					/>
				);

			case ClaimScreen.LABEL_EDIT:
				return (
					<AddEditLabelFormCard
						formData={formData as LabelFormData}
						onBack={goBack}
						onSave={handleSaveLabel}
						selectedLabel={labelEdit}
						edit
					/>
				);

			default:
				return (
					<NewArtistFormCard
						formData={formData as ArtistFormData}
						onFormFieldChange={
							onFormFieldChange as FormFieldChange<ArtistFormData>
						}
						onContinue={handleArtistContinue}
						onBack={goBack}
					/>
				);
		}
	}, [
		artistComment,
		artistEdit,
		artistReleases,
		artistSelectedReleaseIds,
		claimType,
		currentScreen,
		formData,
		goBack,
		goNext,
		handleAddLabel,
		handleAddManagerArtist,
		handleAllSelectedReleaseIdsChange,
		handleArtistCommentChange,
		handleArtistContinue,
		handleClaimTypeSave,
		handleEditLabel,
		handleEditManagerArtist,
		handleSaveLabel,
		handleSaveManagerArtist,
		handleSelectedReleaseIdsChange,
		handleSubmit,
		hasDiscographyRights,
		hasTakedownReleases,
		isLoadingArtistDiscography,
		isSubmitting,
		labelEdit,
		lockedReleaseIdSet,
		onFormFieldChange,
		setClaimType,
	]);

	return screenContent;
};

ClaimAccess.getLayout = (page: React.ReactElement) => {
	return <NavigationLayout>{page}</NavigationLayout>;
};

export const getStaticProps = getServerSideTranslationsWrapper(() => {
	return {
		props: {
			logged: true,
			mode: 'light',
		},
	};
});

export default ClaimAccess;
